import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { WeekGrid } from "./WeekGrid";
import { DayList } from "./DayList";
import { scrollToId } from "../../hooks/useLenis";
import { useLang } from "../../lib/i18n";

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }}
      transition={{ duration: 1.05, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ content, onOpen }) => {
  const ref = useRef(null);
  const { t, lang, pick } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 20 });
  const sry = useSpring(ry, { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 3);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 2.5);
  };
  const reset = () => { rx.set(0); ry.set(0); };

  return (
    <section ref={ref} id="schedule" className="relative overflow-hidden pt-24 lg:pt-28" data-testid="hero-section">
      <motion.div style={{ y: imgY }} aria-hidden="true"
        className="hero-photo pointer-events-none absolute end-0 top-0 h-[560px] w-full sm:w-[62%] lg:w-[46%]">
        {content.images.hero && <img src={content.images.hero} alt="" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-[50%_20%]" />}
      </motion.div>
      <div aria-hidden="true" className="spotlight pointer-events-none absolute inset-0" />

      <div className="relative mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="grid items-end gap-8 pb-10 lg:grid-cols-12 lg:pb-12">
          <div className="lg:col-span-8">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="mb-5 flex items-center gap-3 font-mono text-[13px] uppercase tracking-[0.1em] text-accent sm:text-[15px] sm:tracking-[0.22em]">
              <span className="hidden h-px w-10 shrink-0 bg-accent sm:block" /> {t.heroEyebrow}
            </motion.p>
            <h1 className="font-display text-[2.1rem] font-bold uppercase leading-[0.95] tracking-[-0.02em] text-white sm:text-6xl lg:text-[4.6rem] xl:text-[5.2rem]" data-testid="hero-title">
              {t.heroLines.map((l, i) => <MaskLine key={`${lang}-${l}`} i={i}>{i === 1 ? <span className={lang === "ar" ? "text-accent" : "text-outline"}>{l}</span> : l}</MaskLine>)}
            </h1>
          </div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="lg:col-span-4 lg:pb-3">
            <p className="max-w-md text-base leading-relaxed text-zinc-300">
              {t.heroText}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-2 text-sm text-zinc-200 backdrop-blur" data-testid="hero-location">
                <MapPin size={14} className="text-accent2" /> {pick(content.contact, "location")}
              </span>
              <button onClick={() => scrollToId("rates")} data-testid="hero-see-rates-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-accent hover:text-accent">
                {t.seeRates} <ArrowDown size={14} />
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          style={{ perspective: 1600 }} className="hidden lg:block">
          <motion.div style={{ rotateX: srx, rotateY: sry }} onMouseMove={onMove} onMouseLeave={reset}>
            <WeekGrid classes={content.classes} onOpen={onOpen} />
          </motion.div>
        </motion.div>
        <div className="lg:hidden">
          <DayList classes={content.classes} onOpen={onOpen} />
        </div>
      </div>
    </section>
  );
};
